import { useState, useContext, useEffect } from 'react';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';

import Tr from '../shared/tr';
import { ExportFormats } from '../shared/common';
import { AppContext } from '../App';
import { meshExporter, IExportItem, IExportOrientation } from '../model/MeshExporter';
import SVG_export_ply from '../../assets/image/export_ply.svg';
import SVG_export_stl from '../../assets/image/export_stl.svg';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: 640,
      height: 450,
    },
    btn: {
      margin: 'auto',
      marginBottom: 20,
      textTransform: 'none',
      background: '#E2F8FC',
      color: '#000000',
      '&:hover': {
        background: '#36B0C9',
        color: '#ffffff',
      },
      minWidth: 80,
      minHeight: 32,
    },
    radio: {
      '&$checked': {
        color: '#36B0C9',
      },
    },
    checked: {},
    hidden: {
      display: 'none',
    },
    closeBtn: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: 'black',
    },
    optionTable: {
      margin: '0 60px',
      borderSpacing: '0',
      '&.disabled': {
        opacity: '0.6',
      },
      '& tr:nth-child(odd)': {
        background: '#eee',
      },
      '&:not(.disabled) td > label': {
        cursor: 'pointer',
      },
      '& td > label > span': {
        padding: '4px',
      },
    },
    imgRadio: {
      padding: '0 8px',
      '& > span': {
        position: 'relative',
        bottom: '25px',
      },
    },
  })
);

interface ExportDlgProps {
  open: boolean;
  onClose: () => void;
}

const orientations: Array<IExportOrientation> = [
  { title: '3Shape', x: 90, y: 0, z: 0 },
  { title: 'exocad', x: 0, y: 0, z: 0 },
  { title: 'Dental Wings', x: 90, y: 0, z: 0 },
];

function ExportDlg(props: ExportDlgProps) {
  const tr = new Tr();
  const classes = useStyles();
  const [expFormat, setExpFormat] = useState<ExportFormats>(ExportFormats.STL);
  const { normalMeshes } = useContext(AppContext);
  const [exportList, setExportList] = useState<IExportItem[]>([]);
  const [enableOrientations, setEnableOrientations] = useState<boolean>(false);
  const [orientation, setOrientation] = useState<IExportOrientation>(null);

  useEffect(() => {
    if (props.open) {
      // clear on open
      setExportList([]);
    }
  }, [props.open]);

  const handleRedioSelectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExpFormat(event.target.value as ExportFormats);
  };

  const handleCheckboxSelectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableOrientations(event.target.checked);
    if (!event.target.checked) {
      setOrientation(null);
    }
  };
  const handleOrientationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrientation(orientations.find((option) => option.title === event.target.value));
  };

  const handleExport = () => {
    setExportList([]);
    const transformRotation = orientation ? [orientation.x, orientation.y, orientation.z] : null;
    normalMeshes.forEach((mesh) => {
      const item = meshExporter.exportMesh(mesh, expFormat, transformRotation);
      if (item) {
        setExportList((oldList) => [...oldList, item]);
      }
    });
    handleClose();
  };

  const handleRef = (ref: any) => {
    if (ref?.href?.length > 0) {
      ref.click?.();
    }
  };

  const createDownloadLink = (item: IExportItem, index: number) => {
    const hasTexture = item.texture?.name?.length > 0 && item.texture?.url?.length > 0;
    return (
      <>
        <a className={classes.hidden} href={item.url} download={item.name} ref={(e) => handleRef(e)} key={index} />
        {hasTexture && (
          <a
            className={classes.hidden}
            href={item.texture.url}
            download={item.texture.name}
            ref={(e) => handleRef(e)}
            key={index + '_texture'}
          />
        )}
      </>
    );
  };

  const handleClose = () => {
    props?.onClose?.();
  };

  const createRadio = (radioValue: string, title: string) => {
    return (
      <Grid item xs={5}>
        <label className={classes.imgRadio}>
          <Radio
            checked={radioValue === expFormat}
            onChange={handleRedioSelectedChange}
            value={radioValue}
            name="radio-button"
            classes={{ root: classes.radio, checked: classes.checked }}
          />
          {radioValue === 'stl' ? <img src={SVG_export_stl} alt="" /> : <img src={SVG_export_ply} alt="" />}
        </label>
      </Grid>
    );
  };

  const createCloseBtn = () => {
    return (
      <IconButton aria-label="close" className={classes.closeBtn} onClick={handleClose}>
        <CloseIcon />
      </IconButton>
    );
  };
  const createOrientationOptions = () => {
    return (
      <RadioGroup aria-label="selectOrientation" name="selectOrientation" onChange={handleOrientationChange}>
        <table className={`${classes.optionTable} ${enableOrientations ? '' : 'disabled'}`}>
          <tbody>
            {orientations.map((option) => {
              return (
                <tr key={option.title}>
                  <td width="350px">
                    <label>
                      <Radio
                        disabled={!enableOrientations}
                        value={option.title}
                        name="radio-button"
                        classes={{ root: classes.radio, checked: classes.checked }}
                        checked={enableOrientations && option.title === orientation?.title}
                      />
                      {option.title}
                    </label>
                  </td>
                  <td width="50px">{option.x}</td>
                  <td width="50px">{option.y}</td>
                  <td width="50px">{option.z}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RadioGroup>
    );
  };
  return (
    <Dialog
      open={props.open}
      classes={{
        paper: classes.paper,
      }}
    >
      <DialogTitle>
        {tr.titlebarExport()}
        {createCloseBtn()}
      </DialogTitle>
      <Divider variant="middle" />
      <DialogContent>
        <Grid item xs={12} container spacing={1}>
          <Grid item xs={12}>
            <Typography style={{ marginLeft: 10, fontWeight: 'bold' }}>{tr.exportFormat()}</Typography>
          </Grid>
          <Grid container>
            {createRadio(ExportFormats.STL, tr.stlWithDesc())}
            {createRadio(ExportFormats.PLY, tr.plyWithDesc())}
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ marginLeft: 10, fontWeight: 'bold' }}>{tr.exportOrientationOptions()}</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              style={{ padding: '0 15px' }}
              control={
                <Checkbox
                  checked={enableOrientations}
                  onChange={handleCheckboxSelectedChange}
                  name="enableOrientationOptions"
                  style={{ color: '#36b0c9' }}
                />
              }
              label={tr.exportEnableOrientationLabel()}
            />
          </Grid>
          <Grid>{createOrientationOptions()}</Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus className={classes.btn} style={{ marginRight: 10 }}>
          {tr.generalCancel()}
        </Button>
        <Button onClick={handleExport} color="primary" autoFocus className={classes.btn}>
          {tr.titlebarExport()}
        </Button>
        {exportList.map((e, index) => createDownloadLink(e, index))}
      </DialogActions>
    </Dialog>
  );
}

export default ExportDlg;
